import { useEffect, useMemo } from 'react';
import { Box, Flex, Group, Loader, Stack, Text } from '@mantine/core';
import type { ReactElement } from 'react';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { SessionDriftOverviewViewModel } from './SessionDriftOverviewViewModel';
import type { AnalyticsSurface } from './AnalyticsSurface';
import { formatCount, formatTokens, formatUsd } from '@/shared/analytics/MetricFormatting';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import PageLevelFilterRow from './PageLevelFilterRow';
import { type StatItem } from '@/design/StatBand';
import DriftUserGrid from './DriftUserGrid';
import TopicSummaryPanel from './TopicSummaryPanel';
import TopicInsightsPanel from './TopicInsightsPanel';
import { deriveCallouts } from './TopicNarration';
import type { TopicDefinition } from './TopicCatalogTypes';
import TopicNotFound from './TopicNotFound';
import { SurfaceAssistToolbar } from './assist/SurfaceAssistScope';
import { useAssistFilters } from './assist/AssistSurfaceProvider';

/**
 * The people-first L1 for Session Drift: everyone with at least one drifted
 * session, ranked by the context tokens their drift burned. Replaces the
 * generic alerts list (one row per session) with a per-person roll-up that
 * drills into each person's sessions.
 */
export default function SessionDriftOverviewBody({ surface }: { surface: AnalyticsSurface }): ReactElement {
    const vm = useViewModel(SessionDriftOverviewViewModel);
    const filters = useAssistFilters();

    useEffect(() => {
        void vm.load(surface.id, 'session-drift', filters);
    }, [vm, surface.id, filters]);

    const definition = vm.definition;
    const insightsDefinition = useMemo<TopicDefinition | null>(
        () => (definition === null ? null : { ...definition, primaryField: 'user_id', headlineMetric: 'totalTokens', unitLabel: 'drift tokens', lensLabel: 'person', lensPlural: 'people' }),
        [definition],
    );
    const callouts = useMemo(() => (insightsDefinition === null || vm.insights === null ? [] : deriveCallouts(insightsDefinition, vm.insights)), [insightsDefinition, vm.insights]);

    if (vm.notFound) return <TopicNotFound />;
    if (definition === null) {
        return (
            <Group justify="center" gap="sm" py="xl">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                    Loading session drift…
                </Text>
            </Group>
        );
    }

    const totalDriftTokens = vm.rows.reduce((sum, row) => sum + row.driftTokens, 0);
    const totalDriftCost = vm.rows.reduce((sum, row) => sum + row.driftCostUsd, 0);
    const stats: StatItem[] = [
        { label: 'People drifting', value: formatCount(vm.rows.length) },
        { label: 'Drifted sessions', value: formatCount(vm.driftedSessionCount) },
        { label: 'Drift tokens', value: formatTokens(totalDriftTokens) },
        { label: 'Drift cost', value: formatUsd(totalDriftCost) },
    ];

    return (
        <Box pb="xl">
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            <PageHeaderComponent
                icon={definition.icon}
                eyebrow="Session drift"
                title={definition.title}
                subtitle="Who spreads a single session across unrelated topics — and the context tokens that drift burns re-establishing each pivot."
            />

            {vm.errorMessage !== null && (
                <Text size="sm" c="red" mb="md">
                    {vm.errorMessage}
                </Text>
            )}

            {vm.loading && vm.rows.length === 0 ?
                <Group justify="center" gap="sm" py="xl">
                    <Loader size="sm" />
                </Group>
            : vm.rows.length === 0 ?
                <Box style={{ border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', padding: '14px 16px', background: 'var(--bm-color-surface)' }}>
                    <Text size="sm" c="dimmed">
                        No session drift detected in this window.
                    </Text>
                </Box>
            :   <Flex gap="lg" align="flex-start" direction={{ base: 'column', lg: 'row' }}>
                    <Box style={{ flex: 1, minWidth: 0, width: '100%' }}>
                        <DriftUserGrid
                            rows={vm.rows}
                            hrefFor={(userId) => `${surface.basePath}/${definition.id}/${encodeURIComponent(userId)}`}
                            persistenceKey={`drift-users:${surface.id}:${definition.id}`}
                        />
                    </Box>
                    <Box style={{ width: 300, flexShrink: 0, maxWidth: '100%' }}>
                        <Stack gap="md">
                            <TopicSummaryPanel stats={stats} />
                            {insightsDefinition !== null && vm.insights !== null && (
                                <TopicInsightsPanel surfaceTitle={surface.title} definition={insightsDefinition} primary={vm.insights} derived={callouts} />
                            )}
                        </Stack>
                    </Box>
                </Flex>
            }
        </Box>
    );
}
